import { action, computed, makeAutoObservable, observable } from 'mobx';

import { GQL } from '../../../../../../../gql/client';
import {
  Finish3dSecureCardInfo,
  FixTreatmentInput,
  FixTreatmentMutation,
  FixTreatmentMutationVariables,
  GetTemporallyTreatmentQuery,
  GetTemporallyTreatmentQueryVariables,
  Maybe,
  ShouldConfirmQuery,
  ShouldConfirmQueryVariables,
} from '../../../../../../../gql/gql-types';
import fixTreatment from '../../../../../../../gql/operations/fixTreatment';
import { getTemporallyTreatment } from '../../../../../../../gql/operations/getTemporallyTreatment';
import { shouldConfirm } from '../../../../../../../gql/operations/shouldConfirm';
import { HandledError } from '../../../../../../components/ErrorDialog';
import { Routes } from '../../../../../../constants/Routes';
import { Address } from '../../../../../../domains/user/Address';
import { InsuranceCardStore } from '../../../../AccountDetail/AccountEdit/components/InsuranceCardInput/stores/InsuranceCardStore';
import { CreditCardStore } from '../../../../store/CreditCardStore';
import { Name } from '../../../stores/ProfileStore';

export const RESERVATION_FIX_STORAGE_KEY_PREFIX = 'reservationFixInput:';

type Clinic = {
  id: string;
  name: string;
  termsUrl?: Maybe<string>;
};

export class ReservationTemporallyStore {
  constructor() {
    makeAutoObservable(this, {
      clinic: observable,
      fetch: action,
      concentRequired: observable,
      name: observable,
      setName: action,
      valid: computed,
      newComer: observable,
      setNewComer: action,
      department: observable,
      fever: computed,
      phone: observable,
      address: observable,
      fix: action,
      id: observable,
      insuranceCards: observable,
      setAddress: action,
      setPhone: action,
      profession: observable,
      setProfession: action,
      errors: computed,
      mail: observable,
      setMail: action,
      card: observable,
      setCard: action,
    });
  }

  public clinic?: Clinic = undefined;
  public concentRequired?: boolean | null = undefined;
  public newComer?: boolean = undefined;
  public name?: Name = undefined;
  public id?: string = undefined;
  public department?: string = undefined;
  public treatmentKindId?: string = undefined;

  public get fixable() {
    return this.valid && !this.concentRequired;
  }

  public async fetch(id: string) {
    const treatment = await GQL.queryAsGuest<GetTemporallyTreatmentQueryVariables, GetTemporallyTreatmentQuery>(
      getTemporallyTreatment,
      { id },
    );

    if (!treatment.getTemporallyTreatment) {
      throw new HandledError(
        '原因は15分以内に予約が完了されなかったためと考えられます。お手数ですが、再度はじめから予約をお願い致します。',
      );
    }

    this.concentRequired = (
      await GQL.query<ShouldConfirmQueryVariables, ShouldConfirmQuery>(shouldConfirm, {
        input: {
          clinic: treatment.getTemporallyTreatment.clinic?.id || '00',
        },
      })
    ).shouldConfirm?.confirmRequired;

    this.clinic = treatment.getTemporallyTreatment.clinic || undefined;

    this.id = id;

    this.department = treatment.getTemporallyTreatment.department?.id;
    this.treatmentKindId = treatment.getTemporallyTreatment.treatmentKind?.id;

    this.insuranceCards.setUserId(id);
  }

  public toFixInput(): FixTreatmentInput {
    if (!this.id) {
      throw new HandledError('予約情報が見つかりませんでした。再度はじめからやり直してください。');
    }

    return {
      id: this.id,
      // eslint-disable-next-line no-irregular-whitespace
      name: this.name ? `${this.name.sei}　${this.name.mei}` : '',
      sei: this.name?.sei,
      mei: this.name?.mei,
      mail: this.mail,
      addressLine1: this.address.addressLine1,
      addressLine2: this.address.addressLine2,
      profession: this.profession,
      phone: this.phone,
      zipCode: this.address.zipCode,
      insuranceCards: this.insuranceCards.toInput(),
      pref: this.address.pref,
      cardToken: this.card?.cardToken,
      brand: this.card?.brand,
      expAt: this.card?.expAt,
      fourDigit: this.card?.fourDigit,
    };
  }

  public async fix() {
    const res = await GQL.query<FixTreatmentMutationVariables, FixTreatmentMutation>(fixTreatment, {
      input: this.toFixInput(),
    });

    return res.fixTreatment?.id;
  }

  public setName(name: Name) {
    this.name = name;
  }

  public setNewComer(required: boolean) {
    this.newComer = required;
  }

  public get errors() {
    let errors: { field: string; error: string }[] = [];
    if (this.newComer) {
      if (!this.name) {
        return [{ field: 'sei', error: '氏名を入力してください。' }];
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.sei)) {
        errors = [...errors, { field: 'sei', error: '氏名は全角カタカナで入力してください。' }];
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.mei)) {
        errors = [...errors, { field: 'mei', error: '氏名は全角カタカナで入力してください。' }];
      }

      if (!this.phone) {
        errors = [...errors, { field: 'phone', error: '電話番号を入力してください。' }];
      } else {
        if (this.phone.startsWith('+81')) {
          const localNumber = this.phone.substring(3);
          if (!/^\d{9,10}$/.test(localNumber)) {
            errors = [...errors, { field: 'phone', error: '10桁または11桁で入力してください。' }];
          }
        } else {
          if (!/^\+\d{2,}\d{7,11}$/.test(this.phone)) {
            errors = [...errors, { field: 'phone', error: '電話番号の桁数を確認してください。' }];
          }
        }
      }
    }

    if (this.fever && this.newComer && this.insuranceCards.urls.length <= 0) {
      errors = [...errors, { field: 'insuranceCard', error: '保険証画像をアップロードしてください。' }];
    }

    return errors;
  }

  public get valid() {
    return this.errors.length === 0;
  }

  public get fever() {
    return this.department === '09'; // TODO 09ではなく設定を持つ
  }

  public mail?: string = undefined;

  public setMail(mail: string) {
    this.mail = mail;
  }

  // fever
  public phone?: string = undefined;

  public setPhone(phone: string) {
    this.phone = phone;
  }

  public address: Address = {
    addressLine1: '',
    addressLine2: '',
    pref: '',
    zipCode: '',
  };

  public setAddress(address: Address) {
    this.address = address;
  }

  public insuranceCards: InsuranceCardStore = new InsuranceCardStore();

  public profession?: string = undefined;

  public setProfession(profession: string) {
    this.profession = profession;
  }

  public card?: Card = undefined;

  public setCard(card: Card) {
    this.card = card;
  }

  public cardStatus: CreditCardStore = new CreditCardStore();

  /**
   * 予約途中の入力内容を一時保存する
   * 3Dセキュアなどの画面遷移を挟んでも復元できるようにする
   */
  public saveToSession() {
    const data = {
      clinic: this.clinic,
      concentRequired: this.concentRequired,
      newComer: this.newComer,
      name: this.name,
      id: this.id,
      department: this.department,
      treatmentKindId: this.treatmentKindId,
      mail: this.mail,
      phone: this.phone,
      address: this.address,
      profession: this.profession,
      insuranceCards: this.insuranceCards.toInput(),
    };
    sessionStorage.setItem('tempReservation', JSON.stringify(data));
  }

  /**
   * 一時保存した内容を復元する
   */
  public loadFromSession() {
    const raw = sessionStorage.getItem('tempReservation');
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      this.id = data.id;
      this.clinic = data.clinic;
      this.concentRequired = data.concentRequired;
      this.newComer = data.newComer;
      this.department = data.department;
      this.treatmentKindId = data.treatmentKindId;

      data.name && this.setName(data.name);
      data.phone && this.setPhone(data.phone);
      data.mail && this.setMail(data.mail);
      data.address && this.setAddress(data.address);
      data.profession && this.setProfession(data.profession);

      // 仮予約後のアカウント登録で保険証の登録も必須にする場合、insuranceCardsはstoreのメソッドで復元する必要がある
      if (data.insuranceCards) {
        // classのまま壊さず中身だけ戻す
        this.insuranceCards.urls = data.insuranceCards.urls ?? [];
      }
    } catch (e) {
      console.error('Failed to load temporally reservation from session:', e);
      throw e;
    }
  }

  /**
   * 登録／確定が完了したらセッションを削除する
   */
  public clearSession() {
    sessionStorage.removeItem('tempReservation');
  }

  /**
   * 3Dセキュア完了後の処理
   * 他のstoreにも同様のメソッドを実装しているが、各storeでメソッド名やフローが異なるため処理内容の統一はしていない
   */
  public async completeAfter3DSecure(params: {
    temporallyId: string | null;
    gclid: string | null;
    cardInfo?: Finish3dSecureCardInfo | null;
  }): Promise<{
    redirectPath: string;
    fixedId?: string;
    toastMessage: string;
  }> {
    try {
      if (params.cardInfo) {
        this.setCard({
          cardToken: params.cardInfo.payjpID,
          brand: params.cardInfo.brand,
          expAt: params.cardInfo.expAt,
          fourDigit: params.cardInfo.fourDigit,
        });
      }
      // 1. 仮予約の最新状態をサーバから同期
      params.temporallyId && (await this.fetch(params.temporallyId));

      // 2. ユーザーが途中まで入力してた内容を復元
      this.loadFromSession();

      // 3. fix() で本予約を確定させる
      const fixedId = await this.fix();

      // 4. 成功時の遷移先・トースト文面を作成
      const gclidParam = params.gclid !== '' ? `?gclid=${params.gclid}` : '';
      const toastMessage = 'ご予約が確定しました。';

      return {
        redirectPath: `${Routes.Paths.ReservationFirstSuccess}${gclidParam}`,
        fixedId,
        toastMessage,
      };
    } finally {
      // 成功でも失敗でもセッション上の一時データは掃除する
      this.clearSession();
    }
  }
}

type Card = {
  cardToken: string;
  brand: string;
  fourDigit: string;
  expAt: string;
};
